// filepath: apps/web/src/editors/form-builder/FormulaInput.tsx
//
// FR-UI-BLD-1 AC-2 — reusable debounced formula input.
//
// 250ms debounce from last keystroke. On validation success renders
// a green check + tooltip. On validation failure renders a red
// error icon + tooltip carrying the server's error message.
// INV-9: error message is rendered as a text node, never as HTML.

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormulaValidate } from "./queries";
import type { FormulaValidateRequest } from "./types";

interface FormulaInputProps {
  fieldId: string;
  context: FormulaValidateRequest["context"];
  value: string;
  onChange: (next: string) => void;
  ariaLabel?: string;
}

const DEBOUNCE_MS = 250;

export function FormulaInput({
  fieldId,
  context,
  value,
  onChange,
  ariaLabel,
}: FormulaInputProps): React.ReactElement {
  const { t } = useTranslation();
  const [debouncedValue, setDebouncedValue] = useState(value);
  const mutation = useFormulaValidate();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value]);

  useEffect(() => {
    if (debouncedValue.length === 0) {
      return;
    }
    mutation.mutate({
      formula: debouncedValue,
      context,
      field_id: fieldId,
    });
    // mutation function identity is stable; we intentionally
    // depend on debouncedValue + fieldId + context only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, fieldId, context]);

  const status =
    mutation.data?.ok === true
      ? "ok"
      : mutation.data?.ok === false
        ? "error"
        : "idle";

  return (
    <div
      className="formula-input"
      data-status={status}
      data-testid="formula-input"
    >
      <input
        type="text"
        className="formula-input__text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel ?? "Formula"}
        aria-invalid={status === "error"}
        data-testid="formula-input-text"
      />
      <span
        className="formula-input__status"
        aria-live="polite"
        data-testid="formula-input-status"
      >
        {status === "ok" && (
          <span
            className="formula-input__ok"
            title={t("admin.builder.formula.ok")}
          >
            ✓
          </span>
        )}
        {status === "error" && (
          <span
            className="formula-input__error"
            title={mutation.data?.error ?? t("admin.builder.formula.error")}
          >
            ✗
          </span>
        )}
      </span>
    </div>
  );
}
